import api, { handleResponse, handleError } from './api';

const walletService = {
  /**
   * Get the current user's wallet
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getWallet() {
    try {
      const response = await api.get('/wallets/me');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get wallet with recent transactions
   * @param {number} transactionLimit - Number of transactions to include
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getWalletSummary(transactionLimit = 10) {
    try {
      const response = await api.get(`/wallets/me/summary?transactionLimit=${transactionLimit}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get transaction history
   * @param {number} skip - Number of transactions to skip
   * @param {number} take - Number of transactions to return
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getTransactions(skip = 0, take = 20) {
    try {
      const response = await api.get(`/wallets/me/transactions?skip=${skip}&take=${take}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Check if user has sufficient balance
   * @param {number} amount - Amount to check in $ cents
   * @returns {Promise<{success: boolean, data: {sufficient: boolean, requiredAmount: number}|null, error: string|null}>}
   */
  async checkBalance(amount) {
    try {
      const response = await api.get(`/wallets/me/check-balance?amount=${amount}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Top up the wallet (legacy - direct top-up without payment)
   * @param {number} amount - Amount in cents (100 = $1.00)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async topUp(amount) {
    try {
      const response = await api.post('/wallets/me/top-up', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a Stripe Checkout session for topping up the wallet
   * @param {number} amount - Amount in cents (100 = $1.00)
   * @returns {Promise<{success: boolean, data: {sessionId: string, url: string}|null, error: string|null}>}
   */
  async createCheckoutSession(amount) {
    try {
      const response = await api.post('/wallets/me/top-up/checkout', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a Stripe PaymentIntent for embedded wallet top-up
   * @param {number} amount - Amount in cents (100 = $1.00)
   * @returns {Promise<{success: boolean, data: {clientSecret: string, paymentIntentId: string}|null, error: string|null}>}
   */
  async createPaymentIntent(amount) {
    try {
      const response = await api.post('/wallets/me/top-up/payment-intent', { amount });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Confirm a wallet top-up after Stripe payment succeeds (fallback for webhook)
   * @param {string} paymentIntentId - Stripe PaymentIntent ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async confirmTopUp(paymentIntentId) {
    try {
      const response = await api.post('/wallets/me/top-up/confirm', { paymentIntentId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get wallet feature flags
   * @returns {Promise<{success: boolean, data: {p2pTransfersEnabled: boolean}|null, error: string|null}>}
   */
  async getFeatures() {
    try {
      const response = await api.get('/wallets/features');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Send money to another user
   * @param {string} recipientUserId - Recipient user ID
   * @param {number} amount - Amount in cents
   * @param {string|null} note - Optional note (max 200 chars)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async sendPCoins(recipientUserId, amount, note = null) {
    try {
      const response = await api.post('/wallets/me/send', { recipientUserId, amount, note });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Check cash-out eligibility: Stripe payout setup complete + no pending commitment (same for everyone).
   */
  async getGuestCashOutEligibility() {
    try {
      const response = await api.get('/wallets/me/cashout/eligibility');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Past cash-outs with their status (processing / completed / failed) and fee.
   */
  async getWithdrawals(skip = 0, take = 20) {
    try {
      const response = await api.get('/wallets/me/withdrawals', { params: { skip, take } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Preview a cash-out — Stripe's payout fee passed through at cost + net amount.
   * (Oct 2026: one path for everyone; the "guest" in the name is historical.)
   */
  async previewGuestCashOut(amountCents) {
    try {
      const response = await api.post('/wallets/me/cashout/preview', { amount: amountCents });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Execute a cash-out — a Stripe Connect transfer to the person's Express account through the
   * durable coordinator. Server re-verifies eligibility; this is the only way to move money out.
   */
  async executeGuestCashOut(amountCents, requestId = null) {
    try {
      const response = await api.post('/wallets/me/cashout', { amount: amountCents, requestId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Format a cent amount as "$10.00" (plain dollars — Wallet is the brand now).
   */
  formatAmount(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  },

  /**
   * Format with sign — "+$10.00" / "-$5.00".
   */
  formatAmountWithSign(cents) {
    const sign = cents >= 0 ? '+' : '-';
    const dollars = Math.abs(cents) / 100;
    return `${sign}$${dollars.toFixed(2)}`;
  },
};

export default walletService;
